import type { Element } from "$data/types";

interface ArtworkParams {
  syncPct: number;
  userOrder: string[];
  friendOrder: string[];
  matches: boolean[];
  elements: Element[];
  size?: number;
  brutal?: boolean;
}

/**
 * Build the share-ready mandala artwork as an SVG string.
 * Each of the 5 ring positions hosts both the user's and friend's element;
 * matched positions overlap and glow, mismatches sit at offsets.
 */
export function buildArtwork(p: ArtworkParams): string {
  const size = p.size ?? 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.32;
  const innerR = size * 0.1;
  const harmony = p.syncPct / 100;
  const isBrutal = !!p.brutal;

  const bgFrom = isBrutal ? "#ffffff" : "#0a0a0a";
  const bgTo = isBrutal ? "#ffffff" : p.syncPct >= 70 ? "#0d1729" : "#1a0d1a";

  const byId = new Map(p.elements.map((e) => [e.id, e]));

  let svg = `<svg viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">`;
  svg += `<defs>
    <radialGradient id="bgGrad" cx="50%" cy="40%" r="70%">
      <stop offset="0%" stop-color="${bgTo}"/>
      <stop offset="100%" stop-color="${bgFrom}"/>
    </radialGradient>
    <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="${isBrutal ? 0 : 3}" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>`;
  svg += `<rect width="${size}" height="${size}" fill="url(#bgGrad)"/>`;

  if (!isBrutal) {
    [0.18, 0.28, 0.38].forEach((f) => {
      svg += `<circle cx="${cx}" cy="${cy}" r="${size * f}" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/>`;
    });
  } else {
    svg += `<circle cx="${cx}" cy="${cy}" r="${size * 0.38}" fill="none" stroke="#000000" stroke-width="2"/>`;
  }

  for (let i = 0; i < 5; i++) {
    const angle = -Math.PI / 2 + i * ((2 * Math.PI) / 5);
    const px = cx + r * Math.cos(angle);
    const py = cy + r * Math.sin(angle);
    const matched = p.matches[i] ?? false;
    const userEl = byId.get(p.userOrder[i] ?? "");
    const friendEl = byId.get(p.friendOrder[i] ?? "");

    const shapeSize = matched ? 30 : 22;
    const offset = matched ? 0 : 8;

    if (friendEl) {
      svg += renderElementAt(
        friendEl,
        px + offset,
        py + offset,
        shapeSize,
        0.85,
        isBrutal,
      );
    }
    if (userEl) {
      svg += renderElementAt(
        userEl,
        px - offset,
        py - offset,
        shapeSize,
        matched ? 1 : 0.9,
        isBrutal,
        matched,
      );
    }
    if (!isBrutal && matched) {
      svg += `<circle cx="${px}" cy="${py + 32}" r="2" fill="#22c55e"/>`;
    }
  }

  if (!isBrutal) {
    svg += `<circle cx="${cx}" cy="${cy}" r="${innerR}" fill="rgba(0,0,0,0.6)" stroke="rgba(0,212,255,0.4)" stroke-width="1"/>`;
  } else {
    svg += `<circle cx="${cx}" cy="${cy}" r="${innerR}" fill="#ffffff" stroke="#000000" stroke-width="2"/>`;
  }
  svg += `<text x="${cx}" y="${cy + 4}" text-anchor="middle" font-family="Inter, sans-serif" font-weight="900" font-size="16" fill="${isBrutal ? "#000" : "#fff"}">${p.syncPct}</text>`;

  if (!isBrutal) {
    const particles = Math.round(harmony * 24);
    for (let i = 0; i < particles; i++) {
      const a = Math.random() * Math.PI * 2;
      const rr = size * (0.14 + Math.random() * 0.3);
      const px = cx + rr * Math.cos(a);
      const py = cy + rr * Math.sin(a);
      const op = (0.15 + Math.random() * 0.5).toFixed(2);
      svg += `<circle cx="${px}" cy="${py}" r="0.8" fill="#00d4ff" opacity="${op}"/>`;
    }
  }

  svg += `</svg>`;
  return svg;
}

function renderElementAt(
  el: Element,
  x: number,
  y: number,
  size: number,
  opacity: number,
  isBrutal: boolean,
  glow = false,
): string {
  const half = size / 2;
  const fill = isBrutal ? "#000000" : el.color || "#ffffff";
  const filter = glow && !isBrutal ? 'filter="url(#glow)"' : "";

  if (el.display === "swatch") {
    return `<circle cx="${x}" cy="${y}" r="${half * 0.95}" fill="${el.color}" opacity="${opacity}" ${filter}/>`;
  }
  if (el.display === "text" || (!el.svg && el.label)) {
    const label = (el.short || el.label || "").toString();
    const fontSize = Math.max(
      7,
      Math.min(size * 0.42, (size * 5) / Math.max(label.length, 3)),
    );
    return `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="central" font-family="Inter, system-ui, sans-serif" font-weight="900" font-size="${fontSize.toFixed(1)}" letter-spacing="-0.5" fill="${fill}" opacity="${opacity}" ${filter}>${escapeXml(label)}</text>`;
  }
  const innerSVG = (el.svg || "").replace(/currentColor/g, fill);
  return `<g transform="translate(${x - half} ${y - half}) scale(${size / 100})" opacity="${opacity}" ${filter}>${innerSVG}</g>`;
}

function escapeXml(str: string): string {
  return String(str).replace(/[<>&"']/g, (c) => {
    const m: Record<string, string> = {
      "<": "&lt;",
      ">": "&gt;",
      "&": "&amp;",
      '"': "&quot;",
      "'": "&apos;",
    };
    return m[c]!;
  });
}
